import * as apiErrorHelper from 'epic/apiErrorEpicHelper';
import { quoteOrTabRemoved } from 'epic/workspace/common/tabs/tabsEpics/quoteOrTabRemoved';
import { ofType } from 'redux-observable';
import type { SchedulerLike } from 'rxjs';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, take, takeUntil } from 'rxjs/operators';
import { addonQuoteResultFailed } from 'state/addonForm/addonForm.action';
import * as actionTypes from 'state/addonForm/addonForm.actionTypes';
import type { AddonQuoteResult } from 'state/addonForm/addonForm.model';
import { addonQuoteReceivedThunk } from 'state/addonForm/addonForm.thunks';
import type { EpicWithThunk } from 'state/state.common';
import { throwOnTimeout, wrapError } from 'utils/rxjs';
import { fatEventApiConnector } from 'utils/streaming/api';
import { addonQuoteResult } from 'utils/streaming/messages';

export function addonQuoteResultEpic(appScheduler?: SchedulerLike): EpicWithThunk {
  return (action$, state$, { frontLogger, oldHttpClient, streaming }) =>
    action$.pipe(
      ofType(actionTypes.ADDON_QUOTE_ACKED),
      mergeMap(({ tabId, quoteId, estimatedQuotingDurationInMillis: timeoutMs }) => {
        const quoteOrTabRemoved$ = quoteOrTabRemoved(action$, tabId, quoteId, state$);

        return addonQuoteResult(streaming).pipe(
          filter(event => {
            return event.quoteId === quoteId;
          }),
          mergeMap(lightEvent =>
            fatEventApiConnector(oldHttpClient)
              .getFatEventWithTabId<AddonQuoteResult>(lightEvent, tabId)
              .pipe(
                map(event =>
                  addonQuoteReceivedThunk(tabId, quoteId, event, event.tradeExecutionWindowInMilliseconds ?? 60 * 1000),
                ),
                wrapError(
                  error => new apiErrorHelper.EpicApiError(apiErrorHelper.defaultTechnicalErrorKey, error),
                  appScheduler,
                ),
              ),
          ),
          takeUntil(quoteOrTabRemoved$),
          take(1),
          throwOnTimeout(
            timeoutMs,
            new apiErrorHelper.TimeoutError(timeoutMs, 'quote.errorMessages.responseTimeout'),
            appScheduler,
          ),
          catchError((error: Error) => {
            frontLogger.error('An error occurred during dynamic quote request {@FrontError}', error);
            return of(addonQuoteResultFailed(tabId, quoteId, apiErrorHelper.toApiErrorMessage(error), error));
          }),
        );
      }),
    );
}
